import React from 'react'
import { Form } from 'react-bootstrap'
import { Tooltip, VariantType } from 'react-tooltip'
import styled from 'styled-components'
import uniqid from 'uniqid'
// import { useAtomValue } from 'jotai'
import {
	// Access,
	Rule
} from '../../lib'
// import * as state from '../../states'
import { TRoleAccess } from '../../types'
import { ERoleAccess } from '../../types/enum'
import {
	AccountIcon,
	IconInfo,
	ReportIcon,
	RoleIcon,
	RoleIcon as JobIcon,
	RoleIcon as ProjectIcon,
	SpendIcon,
	TrackingIcon,
	IconSend as PostbackSendIcon,
	IconShare2 as AffiliateIcon,
	IconAward as RevenueIcon,
	IconAward as RevenueAgencyIcon,
	IconExchangeDollar as RevenueSpendIcon,
	IconTrendingUp as RevenueSpendByAccountIcon,
	IconLayers as RevenueSpendByTeamIcon,
} from '../ui'
import { MENU, MENU_CATEGORY } from '../../constants'
import { ALL_LANG_KEY } from '../../types/lang'
import { Trans, TFunction, useTranslation } from 'react-i18next'

type IRoleAccessFieldsProps = {
	rules: TRoleAccess[];
	setRules: React.Dispatch<React.SetStateAction<TRoleAccess[]>>;
}

const AccessRule = styled.div`
	padding: 0px 12px;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 6px;
`

const MyCheck = styled(Form.Check)`
	display: inline-flex;
	& > .form-check-input:disabled[type=checkbox] {
		--bs-form-check-bg-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="none" stroke="%23fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="m6 10 3 3 6-6"/></svg>');
		background-color: var(--mainColor, red) !important;
	}
	& > input.form-check-input[type=checkbox][data-ignored="true"] {
		background-color: red !important;
	}
`

const MyLabel = styled(Form.Label)`
	:has(input[id^="access-"]:disabled) + & {
		opacity: .5;
	}
`

type TRules = {
	$rule: Rule
	$key: ERoleAccess
	name: React.ReactNode
	checked: boolean
	disabled: boolean
	ignored?: boolean
	tooltip?: React.ReactNode
	tooltipVariant?: VariantType
}

// Any (1)
const $rulesAny = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.Any,
		name: `${t(ALL_LANG_KEY.role_access_rule.any_name)}`,
		checked: $rule.$.get(ERoleAccess.Any) ?? false,
		disabled: false,
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.any_tooltip}
		/>,
		tooltipVariant: 'success' as VariantType,
	},
]

// Api (2)
const $rulesApi = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.ApiHealthCheck,
		name: `${t(ALL_LANG_KEY.role_access_rule.api_health_check_name)}`,
		checked: $rule.$.get(ERoleAccess.ApiHealthCheck) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.api_health_check_tooltip}
		/>,
		tooltipVariant: 'success' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.ApiPostbackSend,
		name: `${t(ALL_LANG_KEY.role_access_rule.api_postback_send_name)}`,
		checked: $rule.$.get(ERoleAccess.ApiPostbackSend) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.api_postback_send_tooltip}
			components={{
				icon: <PostbackSendIcon/>,
			}}
		/>,
		tooltipVariant: 'success' as VariantType,
	},
]

// Auth (2)
const $rulesAuth = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.AuthLogin,
		name: `${t(ALL_LANG_KEY.role_access_rule.auth_login_name)}`,
		checked: $rule.$.get(ERoleAccess.AuthLogin) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.auth_login_tooltip}
		/>,
		tooltipVariant: 'success' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AuthProfile,
		name: `${t(ALL_LANG_KEY.role_access_rule.auth_profile_name)}`,
		checked: $rule.$.get(ERoleAccess.AuthProfile) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.auth_profile_tooltip}
			components={{
				br: <br/>
			}}
		/>,
		tooltipVariant: 'success' as VariantType,
	},
]

// Common / Country (3)
const $rulesCommonCountry = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.CommonCountryAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.common_country_any_name)}`,
		checked: $rule.$.get(ERoleAccess.CommonCountryAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.common_country_any_tooltip}
		/>,
		tooltipVariant: 'success' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.CommonCountryList,
		name: `${t(ALL_LANG_KEY.role_access_rule.common_country_list_name)}`,
		checked: $rule.$.get(ERoleAccess.CommonCountryList) ?? false,
		disabled: $rule.CommonCountryAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.common_country_list_tooltip}
			components={{
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'success' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.CommonCountryRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.common_country_read_name)}`,
		checked: $rule.$.get(ERoleAccess.CommonCountryRead) ?? false,
		disabled: $rule.CommonCountryAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.common_country_read_tooltip}
			components={{
				br: <br/>,
			}}
		/>,
		tooltipVariant: 'dark' as VariantType,
	},
]

// Common / Language (3)
const $rulesCommonLanguage = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.CommonLanguageAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.common_language_any_name)}`,
		checked: $rule.$.get(ERoleAccess.CommonLanguageAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.common_language_any_tooltip}
		/>,
		tooltipVariant: 'dark' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.CommonLanguageList,
		name: `${t(ALL_LANG_KEY.role_access_rule.common_language_list_name)}`,
		checked: $rule.$.get(ERoleAccess.CommonLanguageList) ?? false,
		disabled: $rule.CommonLanguageAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.common_language_list_tooltip}
			components={{
				br: <br/>,
			}}
		/>,
		tooltipVariant: 'dark' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.CommonLanguageRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.common_language_read_name)}`,
		checked: $rule.$.get(ERoleAccess.CommonLanguageRead) ?? false,
		disabled: $rule.CommonLanguageAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.common_language_read_tooltip}
			components={{
				br: <br/>,
			}}
		/>,
		tooltipVariant: 'dark' as VariantType,
	},
]

// Common / TimeZone (3)
const $rulesCommonTimeZone = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.CommonTimeZoneAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.common_time_zone_any_name)}`,
		checked: $rule.$.get(ERoleAccess.CommonTimeZoneAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.common_time_zone_any_tooltip}
		/>,
		tooltipVariant: 'dark' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.CommonTimeZoneList,
		name: `${t(ALL_LANG_KEY.role_access_rule.common_time_zone_list_name)}`,
		checked: $rule.$.get(ERoleAccess.CommonTimeZoneList) ?? false,
		disabled: $rule.CommonTimeZoneAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.common_time_zone_list_tooltip}
			components={{
				br: <br/>,
			}}
		/>,
		tooltipVariant: 'dark' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.CommonTimeZoneRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.common_time_zone_read_name)}`,
		checked: $rule.$.get(ERoleAccess.CommonTimeZoneRead) ?? false,
		disabled: $rule.CommonTimeZoneAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.common_time_zone_read_tooltip}
			components={{
				br: <br/>,
			}}
		/>,
		tooltipVariant: 'dark' as VariantType,
	},
]

// Role (6)
const $rulesRole = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.RoleAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.role_any_name)}`,
		checked: $rule.$.get(ERoleAccess.RoleAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.role_any_tooltip}
			components={{
				icon: <RoleIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.role.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RoleList,
		name: `${t(ALL_LANG_KEY.role_access_rule.role_list_name)}`,
		checked: $rule.$.get(ERoleAccess.RoleList) ?? false,
		disabled: $rule.RoleAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.role_list_tooltip}
			components={{
				icon: <RoleIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.role.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RoleRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.role_read_name)}`,
		checked: $rule.$.get(ERoleAccess.RoleRead) ?? false,
		disabled: $rule.RoleAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.role_read_tooltip}
			components={{
				icon: <RoleIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.role.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RoleCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.role_create_name)}`,
		checked: $rule.$.get(ERoleAccess.RoleCreate) ?? false,
		disabled: $rule.RoleAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.role_create_tooltip}
			components={{
				icon: <RoleIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.role.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RoleModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.role_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.RoleModify) ?? false,
		disabled: $rule.RoleAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.role_modify_tooltip}
			components={{
				icon: <RoleIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.role.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RoleRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.role_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.RoleRemove) ?? false,
		disabled: $rule.RoleAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.role_remove_tooltip}
			components={{
				icon: <RoleIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.role.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// Account (7)
const $rulesAccount = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.AccountAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_any_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_any_tooltip}
			components={{
				icon: <AccountIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.account.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountList,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_list_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountList) ?? false,
		disabled: $rule.AccountAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_list_tooltip}
			components={{
				icon: <AccountIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.account.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountIsFiredList,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_is_fired_list_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountIsFiredList) ?? false,
		disabled: $rule.AccountAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_is_fired_list_tooltip}
			components={{
				icon: <AccountIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.account.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_read_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountRead) ?? false,
		disabled: $rule.AccountAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_read_tooltip}
			components={{
				icon: <AccountIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.account.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_create_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountCreate) ?? false,
		disabled: $rule.AccountAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_create_tooltip}
			components={{
				icon: <AccountIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.account.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountModify) ?? false,
		disabled: $rule.AccountAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_modify_tooltip}
			components={{
				icon: <AccountIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.account.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountRemove) ?? false,
		disabled: $rule.AccountAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_remove_tooltip}
			components={{
				icon: <AccountIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.account.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountResurrect,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_resurrect_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountResurrect) ?? false,
		disabled: false,
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_resurrect_tooltip}
			components={{
				icon: <AccountIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.account.title)}`
			}}
		/>,
		tooltipVariant: 'warning' as VariantType,
	},
]

// AccountBuyer (3)
const $rulesAccountBuyer = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.AccountBuyerAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_buyer_any_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountBuyerAny) ?? false,
		disabled: $rule.AccountAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_buyer_any_tooltip}
			components={{
				br: <br/>,
				menuTitle_1: `${t(MENU.offerSpend.title)}`,
				menuTitle_2: `${t(MENU.offerSpendReport.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountBuyerSelf,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_buyer_self_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountBuyerSelf) ?? false,
		disabled: false,
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_buyer_self_tooltip}
			components={{
				br: <br/>,
				menuTitle_1: `${t(MENU.offerSpend.title)}`,
				menuTitle_2: `${t(MENU.offerSpendReport.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountBuyerList,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_buyer_list_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountBuyerList) ?? false,
		disabled: $rule.AccountBuyerAny(),
		ignored: $rule.AccountBuyerSelf(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_buyer_list_tooltip}
			components={{
				br: <br/>,
				menuTitle_1: `${t(MENU.offerSpend.title)}`,
				menuTitle_2: `${t(MENU.offerSpendReport.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// AccountTeam (3)
const $rulesAccountTeam = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.AccountTeamAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_team_any_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountTeamAny) ?? false,
		disabled: $rule.AccountAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_team_any_tooltip}
			components={{
				br: <br/>,
				menuTitle: `${t(MENU.revenueSpendReportByTeam.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountTeamSelf,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_team_self_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountTeamSelf) ?? false,
		disabled: false,
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_team_self_tooltip}
			components={{
				br: <br/>,
				menuTitle: `${t(MENU.revenueSpendReportByTeam.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AccountTeamList,
		name: `${t(ALL_LANG_KEY.role_access_rule.account_team_list_name)}`,
		checked: $rule.$.get(ERoleAccess.AccountTeamList) ?? false,
		disabled: $rule.AccountTeamAny(),
		ignored: $rule.AccountTeamSelf(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.account_team_list_tooltip}
			components={{
				br: <br/>,
				menuTitle: `${t(MENU.revenueSpendReportByTeam.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// Affiliate (6)
const $rulesAffiliate = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.AffiliateAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.affiliate_any_name)}`,
		checked: $rule.$.get(ERoleAccess.AffiliateAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.affiliate_any_tooltip}
			components={{
				icon: <AffiliateIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.affiliate.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AffiliateList,
		name: `${t(ALL_LANG_KEY.role_access_rule.affiliate_list_name)}`,
		checked: $rule.$.get(ERoleAccess.AffiliateList) ?? false,
		disabled: $rule.AffiliateAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.affiliate_list_tooltip}
			components={{
				icon: <AffiliateIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.affiliate.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AffiliateRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.affiliate_read_name)}`,
		checked: $rule.$.get(ERoleAccess.AffiliateRead) ?? false,
		disabled: $rule.AffiliateAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.affiliate_read_tooltip}
			components={{
				icon: <AffiliateIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.affiliate.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AffiliateCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.affiliate_create_name)}`,
		checked: $rule.$.get(ERoleAccess.AffiliateCreate) ?? false,
		disabled: $rule.AffiliateAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.affiliate_create_tooltip}
			components={{
				icon: <AffiliateIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.affiliate.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AffiliateModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.affiliate_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.AffiliateModify) ?? false,
		disabled: $rule.AffiliateAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.affiliate_modify_tooltip}
			components={{
				icon: <AffiliateIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.affiliate.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.AffiliateRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.affiliate_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.AffiliateRemove) ?? false,
		disabled: $rule.AffiliateAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.affiliate_remove_tooltip}
			components={{
				icon: <AffiliateIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.affiliate.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// OfferScope (6)
const $rulesOfferScope = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.OfferScopeAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_scope_any_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferScopeAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_scope_any_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferScopeList,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_scope_list_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferScopeList) ?? false,
		disabled: $rule.OfferScopeAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_scope_list_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferScopeRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_scope_read_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferScopeRead) ?? false,
		disabled: $rule.OfferScopeAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_scope_read_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferScopeCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_scope_create_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferScopeCreate) ?? false,
		disabled: $rule.OfferScopeAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_scope_create_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferScopeModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_scope_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferScopeModify) ?? false,
		disabled: $rule.OfferScopeAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_scope_modify_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferScopeRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_scope_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferScopeRemove) ?? false,
		disabled: $rule.OfferScopeAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_scope_remove_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// Offer (10)
const $rulesOffer = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.OfferAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_any_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_any_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferAnyGroup,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_any_group_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferAnyGroup) ?? false,
		disabled: $rule.OfferAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_any_group_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferList,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_list_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferList) ?? false,
		disabled: $rule.OfferAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_list_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_read_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferRead) ?? false,
		disabled: $rule.OfferAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_read_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_create_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferCreate) ?? false,
		disabled: $rule.OfferAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_create_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferModify) ?? false,
		disabled: $rule.OfferAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_modify_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferRemove) ?? false,
		disabled: $rule.OfferAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_remove_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferResurrect,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_resurrect_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferResurrect) ?? false,
		disabled: $rule.OfferAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_resurrect_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferBind,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_bind_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferBind) ?? false,
		disabled: $rule.OfferAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_bind_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferUnbind,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_unbind_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferUnbind) ?? false,
		disabled: $rule.OfferAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_unbind_tooltip}
			components={{
				icon: <TrackingIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerScope.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// OfferSpend (9)
const $rulesOfferSpend = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.OfferSpendAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_any_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_any_tooltip}
			components={{
				icon: <SpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendAnyAccount,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_any_account_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendAnyAccount) ?? false,
		disabled: $rule.OfferSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_any_account_tooltip}
			components={{
				icon: <SpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendAnyDay,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_any_day_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendAnyDay) ?? false,
		disabled: $rule.OfferSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_any_day_tooltip}
			components={{
				icon: <SpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendAnyGroup,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_any_group_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendAnyGroup) ?? false,
		disabled: $rule.OfferSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_any_group_tooltip}
			components={{
				icon: <SpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendList,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_list_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendList) ?? false,
		disabled: $rule.OfferSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_list_tooltip}
			components={{
				icon: <SpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_read_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendRead) ?? false,
		disabled: $rule.OfferSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_read_tooltip}
			components={{
				icon: <SpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_create_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendCreate) ?? false,
		disabled: $rule.OfferSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_create_tooltip}
			components={{
				icon: <SpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendModify) ?? false,
		disabled: $rule.OfferSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_modify_tooltip}
			components={{
				icon: <SpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendRemove) ?? false,
		disabled: $rule.OfferSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_remove_tooltip}
			components={{
				icon: <SpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// OfferSpendBy (4)
const $rulesOfferSpendBy = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.OfferSpendByAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_by_any_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendByAny) ?? false,
		disabled: $rule.OfferSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_by_any_tooltip}
			components={{
				icon: <ReportIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.offerSpendReport.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendByAccountList,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_by_account_list_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendByAccountList) ?? false,
		disabled: $rule.OfferSpendByAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_by_account_list_tooltip}
			components={{
				icon: <ReportIcon/>,
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendByOfferList,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_by_offer_list_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendByOfferList) ?? false,
		disabled: $rule.OfferSpendByAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_by_offer_list_tooltip}
			components={{
				icon: <ReportIcon/>,
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.OfferSpendByOfferScopeList,
		name: `${t(ALL_LANG_KEY.role_access_rule.offer_spend_by_offer_scope_list_name)}`,
		checked: $rule.$.get(ERoleAccess.OfferSpendByOfferScopeList) ?? false,
		disabled: $rule.OfferSpendByAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.offer_spend_by_offer_scope_list_tooltip}
			components={{
				icon: <ReportIcon/>,
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// Revenue (6)
const $rulesRevenue = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.RevenueAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_any_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueAny) ?? false,
		disabled: $rule.Any(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_any_tooltip}
			components={{
				icon: <></>,
				br: <br/>,
				menuTitle: `${t(MENU_CATEGORY.revenue.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueList,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_list_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueList) ?? false,
		disabled: $rule.RevenueAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_list_tooltip}
			components={{
				icon: <RevenueIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU_CATEGORY.revenue.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_read_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueRead) ?? false,
		disabled: $rule.RevenueAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_read_tooltip}
			components={{
				icon: <RevenueIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU_CATEGORY.revenue.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_create_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueCreate) ?? false,
		disabled: $rule.RevenueAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_create_tooltip}
			components={{
				icon: <RevenueIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU_CATEGORY.revenue.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueModify) ?? false,
		disabled: $rule.RevenueAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_modify_tooltip}
			components={{
				icon: <RevenueIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU_CATEGORY.revenue.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueRemove) ?? false,
		disabled: $rule.RevenueAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_remove_tooltip}
			components={{
				icon: <RevenueIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU_CATEGORY.revenue.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// RevenueAgency (6)
const $rulesRevenueAgency = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.RevenueAgencyAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_agency_any_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueAgencyAny) ?? false,
		disabled: $rule.RevenueAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_agency_any_tooltip}
			components={{
				icon: <RevenueAgencyIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueAgency.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueAgencyList,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_agency_list_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueAgencyList) ?? false,
		disabled: $rule.RevenueAgencyAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_agency_list_tooltip}
			components={{
				icon: <RevenueAgencyIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueAgency.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueAgencyRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_agency_read_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueAgencyRead) ?? false,
		disabled: $rule.RevenueAgencyAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_agency_read_tooltip}
			components={{
				icon: <RevenueAgencyIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueAgency.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueAgencyCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_agency_create_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueAgencyCreate) ?? false,
		disabled: $rule.RevenueAgencyAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_agency_create_tooltip}
			components={{
				icon: <RevenueAgencyIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueAgency.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueAgencyModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_agency_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueAgencyModify) ?? false,
		disabled: $rule.RevenueAgencyAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_agency_modify_tooltip}
			components={{
				icon: <RevenueAgencyIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueAgency.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueAgencyRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_agency_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueAgencyRemove) ?? false,
		disabled: $rule.RevenueAgencyAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_agency_remove_tooltip}
			components={{
				icon: <RevenueAgencyIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueAgency.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// RevenueSpend (9)
const $rulesRevenueSpend = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.RevenueSpendAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_any_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendAny) ?? false,
		disabled: $rule.RevenueAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_any_tooltip}
			components={{
				icon: <RevenueSpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueSpendList,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_list_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendList) ?? false,
		disabled: $rule.RevenueSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_list_tooltip}
			components={{
				icon: <RevenueSpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueSpendRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_read_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendRead) ?? false,
		disabled: $rule.RevenueSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_read_tooltip}
			components={{
				icon: <RevenueSpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueSpendCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_create_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendCreate) ?? false,
		disabled: $rule.RevenueSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_create_tooltip}
			components={{
				icon: <RevenueSpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueSpendModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendModify) ?? false,
		disabled: $rule.RevenueSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_modify_tooltip}
			components={{
				icon: <RevenueSpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueSpendRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendRemove) ?? false,
		disabled: $rule.RevenueSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_remove_tooltip}
			components={{
				icon: <RevenueSpendIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.revenueSpend.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// RevenueSpendBy (5)
const $rulesRevenueSpendBy = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.RevenueSpendByAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_by_any_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendByAny) ?? false,
		disabled: $rule.RevenueSpendAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_by_any_tooltip}
			components={{
				icon: <><RevenueSpendByAccountIcon/><RevenueSpendByTeamIcon/></>,
				br: <br/>,
				menuTitle_1: `${t(MENU.revenueSpendReportByAccount.title)}`,
				menuTitle_2: `${t(MENU.revenueSpendReportByTeam.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueSpendByAccountList,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_by_account_list_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendByAccountList) ?? false,
		disabled: $rule.RevenueSpendByAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_by_account_list_tooltip}
			components={{
				icon: <RevenueSpendByAccountIcon/>,
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueSpendByAccountsList,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_by_accounts_list_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendByAccountsList) ?? false,
		disabled: $rule.RevenueSpendByAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_by_accounts_list_tooltip}
			components={{
				icon: <RevenueSpendByAccountIcon/>,
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueSpendByTeamList,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_by_team_list_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendByTeamList) ?? false,
		disabled: $rule.RevenueSpendByAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_by_team_list_tooltip}
			components={{
				icon: <RevenueSpendByTeamIcon/>,
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.RevenueSpendByTeamsList,
		name: `${t(ALL_LANG_KEY.role_access_rule.revenue_spend_by_teams_list_name)}`,
		checked: $rule.$.get(ERoleAccess.RevenueSpendByTeamsList) ?? false,
		disabled: $rule.RevenueSpendByAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.revenue_spend_by_teams_list_tooltip}
			components={{
				icon: <RevenueSpendByTeamIcon/>,
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// Debug (4)
const $rulesDebug = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.DebugAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.debug_any_name)}`,
		checked: $rule.$.get(ERoleAccess.DebugAny) ?? false,
		disabled: false,
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.debug_any_tooltip}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
	{
		$rule,
		$key: ERoleAccess.DebugCronRunner,
		name: `${t(ALL_LANG_KEY.role_access_rule.debug_cron_runner_name)}`,
		checked: $rule.$.get(ERoleAccess.DebugCronRunner) ?? false,
		disabled: $rule.DebugAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.debug_cron_runner_tooltip}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
	{
		$rule,
		$key: ERoleAccess.DebugDraft,
		name: `${t(ALL_LANG_KEY.role_access_rule.debug_draft_name)}`,
		checked: $rule.$.get(ERoleAccess.DebugDraft) ?? false,
		disabled: $rule.DebugAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.debug_draft_tooltip}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
	{
		$rule,
		$key: ERoleAccess.DebugMessage,
		name: `${t(ALL_LANG_KEY.role_access_rule.debug_message_name)}`,
		checked: $rule.$.get(ERoleAccess.DebugMessage) ?? false,
		disabled: $rule.DebugAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.debug_message_tooltip}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// Job (6)
const $rulesJob = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.JobAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.job_any_name)}`,
		checked: $rule.$.get(ERoleAccess.JobAny) ?? false,
		disabled: $rule.DebugAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.job_any_tooltip}
			components={{
				icon: <JobIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.job.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.JobList,
		name: `${t(ALL_LANG_KEY.role_access_rule.job_list_name)}`,
		checked: $rule.$.get(ERoleAccess.JobList) ?? false,
		disabled: $rule.JobAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.job_list_tooltip}
			components={{
				icon: <JobIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.job.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.JobRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.job_read_name)}`,
		checked: $rule.$.get(ERoleAccess.JobRead) ?? false,
		disabled: $rule.JobAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.job_read_tooltip}
			components={{
				icon: <JobIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.job.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.JobCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.job_create_name)}`,
		checked: $rule.$.get(ERoleAccess.JobCreate) ?? false,
		disabled: $rule.JobAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.job_create_tooltip}
			components={{
				icon: <JobIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.job.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.JobModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.job_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.JobModify) ?? false,
		disabled: $rule.JobAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.job_modify_tooltip}
			components={{
				icon: <JobIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.job.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.JobRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.job_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.JobRemove) ?? false,
		disabled: $rule.JobAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.job_remove_tooltip}
			components={{
				icon: <JobIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.job.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

// Project (6)
const $rulesProject = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	{
		$rule,
		$key: ERoleAccess.ProjectAny,
		name: `${t(ALL_LANG_KEY.role_access_rule.project_any_name)}`,
		checked: $rule.$.get(ERoleAccess.ProjectAny) ?? false,
		disabled: $rule.DebugAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.project_any_tooltip}
			components={{
				icon: <ProjectIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.project.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.ProjectList,
		name: `${t(ALL_LANG_KEY.role_access_rule.project_list_name)}`,
		checked: $rule.$.get(ERoleAccess.ProjectList) ?? false,
		disabled: $rule.ProjectAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.project_list_tooltip}
			components={{
				icon: <ProjectIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.project.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.ProjectRead,
		name: `${t(ALL_LANG_KEY.role_access_rule.project_read_name)}`,
		checked: $rule.$.get(ERoleAccess.ProjectRead) ?? false,
		disabled: $rule.ProjectAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.project_read_tooltip}
			components={{
				icon: <ProjectIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.project.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.ProjectCreate,
		name: `${t(ALL_LANG_KEY.role_access_rule.project_create_name)}`,
		checked: $rule.$.get(ERoleAccess.ProjectCreate) ?? false,
		disabled: $rule.ProjectAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.project_create_tooltip}
			components={{
				icon: <ProjectIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.project.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.ProjectModify,
		name: `${t(ALL_LANG_KEY.role_access_rule.project_modify_name)}`,
		checked: $rule.$.get(ERoleAccess.ProjectModify) ?? false,
		disabled: $rule.ProjectAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.project_modify_tooltip}
			components={{
				icon: <ProjectIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.project.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	}, {
		$rule,
		$key: ERoleAccess.ProjectRemove,
		name: `${t(ALL_LANG_KEY.role_access_rule.project_remove_name)}`,
		checked: $rule.$.get(ERoleAccess.ProjectRemove) ?? false,
		disabled: $rule.ProjectAny(),
		tooltip: <Trans
			i18nKey={ALL_LANG_KEY.role_access_rule.project_remove_tooltip}
			components={{
				icon: <ProjectIcon/>,
				br: <br/>,
				menuTitle: `${t(MENU.project.title)}`
			}}
		/>,
		tooltipVariant: 'info' as VariantType,
	},
]

export const $rules = ($rule: Rule, t: TFunction): Array<TRules | null> => [
	null,
	...$rulesAny($rule, t),
	null,
	...$rulesApi($rule, t),
	null,
	...$rulesAuth($rule, t),
	null,
	...$rulesCommonCountry($rule, t),
	null,
	...$rulesCommonLanguage($rule, t),
	null,
	...$rulesCommonTimeZone($rule, t),
	null,
	...$rulesRole($rule, t),
	null,
	...$rulesAccount($rule, t),
	null,
	...$rulesAccountBuyer($rule, t),
	null,
	...$rulesAccountTeam($rule, t),
	null,
	...$rulesAffiliate($rule, t),
	null,
	...$rulesOfferScope($rule, t),
	null,
	...$rulesOffer($rule, t),
	null,
	...$rulesOfferSpend($rule, t),
	null,
	...$rulesOfferSpendBy($rule, t),
	null,
	...$rulesRevenue($rule, t),
	null,
	...$rulesRevenueAgency($rule, t),
	null,
	...$rulesRevenueSpend($rule, t),
	null,
	...$rulesRevenueSpendBy($rule, t),
	null,
	...$rulesDebug($rule, t),
	null,
	// ...$rulesJob($rule, t),
	// null,
	// ...$rulesProject($rule, t),
	// null,
].filter((_: TRules | null): boolean => !_ || ![
	ERoleAccess.CommonCountryRead,
	ERoleAccess.CommonLanguageRead,
	ERoleAccess.CommonTimeZoneRead,
	ERoleAccess.RoleRead,
	ERoleAccess.AccountRead,
	ERoleAccess.AffiliateRead,
	ERoleAccess.AffiliateCreate,
	ERoleAccess.AffiliateRemove,
	ERoleAccess.OfferScopeRead,
	ERoleAccess.OfferRead,
	ERoleAccess.OfferCreate,
	ERoleAccess.OfferModify,
	ERoleAccess.OfferRemove,
	ERoleAccess.OfferResurrect,
	ERoleAccess.OfferSpendRead,
	ERoleAccess.RevenueList,
	ERoleAccess.RevenueRead,
	ERoleAccess.RevenueCreate,
	ERoleAccess.RevenueModify,
	ERoleAccess.RevenueRemove,
	ERoleAccess.RevenueAgencyRead,
	ERoleAccess.RevenueSpendRead,
	ERoleAccess.RevenueSpendByAccountsList,
	ERoleAccess.RevenueSpendByTeamsList,
	ERoleAccess.JobRead,
	ERoleAccess.ProjectRead,
].includes(_.$key))

export const RoleAccessFields = (
	props: IRoleAccessFieldsProps
) => {
	const { rules, setRules } = props
	const { t } = useTranslation()
	const $rule = React.useMemo(() => new Rule(rules), [rules])
	const items = React.useMemo(() => $rules($rule, t), [$rule, t])

	const doChecked = ($rule: Rule, $key: ERoleAccess) =>
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const checked: boolean = e.target.checked
			const rulesTail: TRoleAccess[] = Rule.$omit($rule.$rules, [$key])
			const rule: TRoleAccess = { $key, $value: checked } as TRoleAccess
			setRules(checked ? [rule, ...rulesTail] : [...rulesTail])
		}

	const renderRule = (item: TRules, index: number) => {
		const id: string = uniqid(item.$key as string, 'rule')
		const tooltipId: string = uniqid(item.$key as string, 'rule-tooltip')
		return <AccessRule key={index}>
			<MyCheck
				checked={item.checked}
				disabled={item.disabled}
				data-ignored={item?.ignored ?? false}
				id={id}
				type="checkbox"
				onChange={doChecked(item.$rule, item.$key)}
			/>
			<MyLabel className="text-dark" htmlFor={id} style={{ marginBottom: 0 }}>
				{item.name}
				&nbsp;
				{
					item?.tooltip
						? <>
							<span data-tooltip-id={tooltipId}><IconInfo /></span>
							<Tooltip id={tooltipId} variant={item?.tooltipVariant ?? 'light'} clickable render={() => {
								return <>{item.tooltip}</>
							}} />
						</>
						: null
				}
			</MyLabel>
		</AccessRule>
	}

	// const profile = useAtomValue(state.profileAtom)
	// const $access = new Access(profile ?? null)
	// const onInput = ($key: ERoleAccess) => 
	// 	(e: React.ChangeEvent<HTMLInputElement>) => {
	// 		const value: string = e.target.value
	// 		const rulesTail: TRoleAccess[] = $rule.$$([$key], false)
	// 		const rule: TRoleAccess | null = $rule.$$([$key], true).at(0) ?? null
	// 		// const ruleNew: TRoleAccess = { $key, $value: rule?.$value ?? false, ...value && { group: value } }
	// 		setRules(rule ? [rule, ...rulesTail] : [...rulesTail])
	// 	}
	// const renderControl = (
	// 	$key: ERoleAccess,
	// ) => {
	// 	const id: string = uniqid($key as string, 'control')
	// 	return <>
	// 		<MyControl
	// 			id={'access-offer-scope-list-group'}
	// 			type="text"
	// 			placeholder="Group"
	// 			value={$rule.$<ERoleAccess.OfferScopeList>(ERoleAccess.OfferScopeList)?.$key ?? ''}
	// 			onChange={onInput($key)}
	// 		/>
	// 	</>
	// }

	return (
		<>
			{
				items.map((_, index) => {
					if (!_ ) {
						return <br key={index} />
					}
					return renderRule(_, index)
				})
			}
		</>
	)
}
